import { Button, Dropdown, Menu } from 'antd';
import { EllipsisIcon } from 'app/components/CommonIcons/CommonIcons';
import CompanyStatusTag from 'app/components/CompanyStatusTag/CompanyStatusTag';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { TextSemibold16 } from 'styles/typography';
import { useCompanySlice } from '../../slice';
import { ICompanyHiikoEnvironmentsData } from '../../slice/types';

import * as S from './CompanyInOtherEnvsTable.styles';

interface IProps {
  envsData?: ICompanyHiikoEnvironmentsData[];
  selectedEnvId: number;
  isOwnerUser: boolean;
}

const CompanyInOtherEnvsTable = ({
  envsData,
  selectedEnvId,
  isOwnerUser,
}: IProps) => {
  const { t } = useTranslation();

  const { actions } = useCompanySlice();
  const dispatch = useDispatch();

  // remove current user environment from table.
  const filteredEnvsData = envsData?.filter(
    envData => envData.hiikoEnvironment.id !== selectedEnvId,
  );

  const openShareCompanyForm = () => {
    dispatch(actions.setShareCompanyFormType({ type: 'share' }));

    dispatch(actions.toggleShareCompanyForm(true));
  };

  const openChangeAccessRightsForm = (
    envData: ICompanyHiikoEnvironmentsData,
  ) => {
    dispatch(
      actions.setShareCompanyFormType({
        type: 'edit',
        id: envData.hiikoEnvironment.id,
        permissions: envData.permissions,
      }),
    );
    dispatch(actions.toggleShareCompanyForm(true));
  };

  const columns = [
    {
      title: t('Company.Details.Table.Name', 'Name'),
      dataIndex: ['hiikoEnvironment', 'name'],
      key: 'name',
      sorter: {
        compare: (
          a: ICompanyHiikoEnvironmentsData,
          b: ICompanyHiikoEnvironmentsData,
        ) => {
          return a.hiikoEnvironment.name.localeCompare(b.hiikoEnvironment.name);
        },
      },
    },
    {
      title: t(
        'Company.Details.Table.EnvironmentOwnerEmail',
        'Environment Owner Email',
      ),
      dataIndex: ['hiikoEnvironment', 'user', 'email'],
      key: 'email',
      sorter: {
        compare: (
          a: ICompanyHiikoEnvironmentsData,
          b: ICompanyHiikoEnvironmentsData,
        ) => {
          return a.hiikoEnvironment.user.email.localeCompare(
            b.hiikoEnvironment.user.email,
          );
        },
      },
    },
    {
      title: t('Company.Details.Table.Status', 'Status'),
      dataIndex: 'accepted',
      key: 'accepted',
      render: (status: boolean) => (
        <CompanyStatusTag status={status} owner={false} />
      ),
      sorter: {
        compare: (
          a: ICompanyHiikoEnvironmentsData,
          b: ICompanyHiikoEnvironmentsData,
        ) => {
          return a.accepted && !b.accepted
            ? 1
            : !a.accepted && b.accepted
            ? -1
            : 0;
        },
      },
    },
    {
      title: t('Common.Actions', 'Actions'),
      dataIndex: 'actions',
      key: 'actions',
      render: (_: unknown, envData: ICompanyHiikoEnvironmentsData) => (
        <Dropdown
          trigger={['click']}
          overlay={
            <Menu>
              <Menu.Item
                onClick={() => {
                  openChangeAccessRightsForm(envData);
                }}
              >
                {t(
                  'Company.Details.Table.ManagePermissions',
                  'ManagePermissions',
                )}
              </Menu.Item>
            </Menu>
          }
        >
          <EllipsisIcon />
        </Dropdown>
      ),
    },
  ];
  return (
    <>
      <S.PermissionsTitleRow align="middle" justify="space-between">
        <TextSemibold16>
          {t(
            'Company.Details.CompanyInOtherEnvironments',
            'Establishment of rights in companies',
          )}
        </TextSemibold16>
        {isOwnerUser && (
          <Button type="primary" onClick={openShareCompanyForm}>
            {t('Company.Details.ShareCompanyData', 'Share company data')}
          </Button>
        )}
      </S.PermissionsTitleRow>

      <StyledTable
        rowKey={hiikoEnvironment => hiikoEnvironment.hiikoEnvironment.id}
        dataSource={filteredEnvsData}
        columns={columns}
        pagination={false}
      />
    </>
  );
};

export default CompanyInOtherEnvsTable;
